import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAuth } from "@/hooks/useAuth";

interface OrderItem {
  nameEn: string;
  nameAr: string;
  imageUrl: string;
  price: number;
  quantity: number;
  variantText: string;
}

interface OrderData {
  orderNumber: string;
  orderDate: string;
  estimatedDelivery: string;
  items: OrderItem[];
  shippingAddress: {
    fullName: string;
    email: string;
    phone: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  subtotal: number;
  shipping: number;
  tax: number;
  total: number;
  itemCount: number;
}

const COUNTRY_NAMES: Record<string, string> = {
  US: "United States",
  CA: "Canada",
  GB: "United Kingdom",
  EG: "Egypt",
  SA: "Saudi Arabia",
  AE: "United Arab Emirates",
  DE: "Germany",
  FR: "France",
  AU: "Australia",
  JP: "Japan",
};

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function CheckoutSuccessPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("generalstore_last_order");
      if (raw) {
        const parsed = JSON.parse(raw);
        setOrder(parsed);
      }
    } catch {
      // corrupted data, ignore
    }
    setLoading(false);
  }, []);

  const formatPrice = (price: number) => `$${price.toFixed(2)}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
        <Navbar />
        <main className="pt-20 pb-16 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="w-full px-4 md:px-6 lg:px-8 py-8">
            <div className="max-w-lg mx-auto text-center py-12">
              <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-background-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
              </div>
              <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-3">
                No Order Found
              </h2>
              <p className="text-foreground-500 mb-8 leading-relaxed text-sm md:text-base">
                We couldn&apos;t find your order information. It may have expired. Please place a new order.
              </p>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-shopping-bag-line"></i>
                {t("success.continue_shopping")}
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-8 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Success header */}
            <div className="text-center mb-10">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center animate-in zoom-in duration-500">
                <i className="ri-check-line text-4xl text-green-500"></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {t("success.title")}
              </h1>
              <p className="text-foreground-500 max-w-md mx-auto leading-relaxed text-sm md:text-base">
                {t("success.subtitle")}
              </p>
              <p className="text-xs text-foreground-400 mt-3">
                {t("success.confirmation_email")}
              </p>
            </div>

            {/* Order details card */}
            <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8 mb-6">
              {/* Order meta */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pb-6 border-b border-background-200">
                <div>
                  <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">
                    {t("success.order_number")}
                  </p>
                  <p className="text-sm font-bold text-foreground-950 font-mono tracking-tight">
                    {order.orderNumber}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">
                    {t("success.order_date")}
                  </p>
                  <p className="text-sm font-medium text-foreground-900">
                    {formatDate(order.orderDate)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">
                    {t("success.estimated_delivery")}
                  </p>
                  <p className="text-sm font-medium text-foreground-900">
                    {formatDate(order.estimatedDelivery)}
                  </p>
                </div>
              </div>

              {/* Order items */}
              <div className="py-6 border-b border-background-200">
                <div className="space-y-4">
                  {order.items.map((item, idx) => {
                    const itemTotal = item.price * item.quantity;
                    return (
                      <div key={idx} className="flex gap-4">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-background-100 flex-shrink-0">
                          <img
                            src={`${item.imageUrl}&width=128&height=128`}
                            alt={isAr ? item.nameAr : item.nameEn}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug">
                            {isAr ? item.nameAr : item.nameEn}
                          </p>
                          {item.variantText && (
                            <p className="text-xs text-foreground-500 mt-0.5">{item.variantText}</p>
                          )}
                          <div className="flex items-center justify-between mt-1.5">
                            <span className="text-xs text-foreground-500">
                              {formatPrice(item.price)} × {item.quantity}
                            </span>
                            <span className="text-sm font-semibold text-foreground-900">
                              {formatPrice(itemTotal)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Shipping address */}
              <div className="py-6 border-b border-background-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-lg text-accent-500"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">
                      {t("success.shipping_to")}
                    </p>
                    <p className="text-sm font-medium text-foreground-900">
                      {order.shippingAddress.fullName}
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {order.shippingAddress.addressLine1}
                      {order.shippingAddress.addressLine2 && `, ${order.shippingAddress.addressLine2}`}
                      <br />
                      {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}
                      <br />
                      {COUNTRY_NAMES[order.shippingAddress.country] || order.shippingAddress.country}
                    </p>
                    <p className="text-xs text-foreground-400 mt-1">{order.shippingAddress.phone}</p>
                  </div>
                </div>
              </div>

              {/* Payment method */}
              <div className="py-6 border-b border-background-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-bank-card-line text-lg text-secondary-500"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">
                      {t("success.payment_method")}
                    </p>
                    <p className="text-sm font-medium text-foreground-900 flex items-center gap-2">
                      <i className="ri-visa-fill text-blue-600 text-lg"></i>
                      {t("success.payment_visa")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Price breakdown */}
              <div className="pt-6 space-y-2.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-foreground-600">
                    {t("checkout.subtotal")} ({order.itemCount === 1 ? t("checkout.items_count", { count: order.itemCount }) : t("checkout.items_count_plural", { count: order.itemCount })})
                  </span>
                  <span className="font-medium text-foreground-900">{formatPrice(order.subtotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-600">{t("checkout.shipping")}</span>
                  <span className={`font-medium ${order.shipping === 0 ? "text-green-600" : "text-foreground-900"}`}>
                    {order.shipping === 0 ? t("checkout.free") : formatPrice(order.shipping)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-600">{t("checkout.tax")}</span>
                  <span className="font-medium text-foreground-900">{formatPrice(order.tax)}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-background-200">
                  <span className="text-base font-semibold text-foreground-950">{t("checkout.total")}</span>
                  <span className="text-xl font-bold text-foreground-950">{formatPrice(order.total)}</span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
              <Link
                to="/products"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-shopping-bag-line"></i>
                {t("success.continue_shopping")}
              </Link>
              {isAuthenticated && (
                <Link
                  to="/account/orders"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 border-2 border-foreground-200 text-foreground-700 font-semibold rounded-full hover:border-foreground-400 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-file-list-3-line"></i>
                  {t("success.view_order")}
                </Link>
              )}
            </div>

            {/* What's next */}
            <div className="max-w-2xl mx-auto bg-background-100/70 border border-background-200/70 rounded-2xl p-6 md:p-8">
              <h3 className="text-base font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-information-line text-accent-500"></i>
                {t("success.whats_next")}
              </h3>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-truck-line text-xs text-accent-500"></i>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t("success.track_info")}
                  </p>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-customer-service-line text-xs text-accent-500"></i>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t("success.questions")}
                    {" "}
                    <a href="mailto:hello@generalstore.com" className="text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap">
                      {t("success.contact_support")}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}